import React, { useState, useRef, useEffect } from 'react'
import { useLanguage } from '../../contexts/LanguageContext'
import { ChevronDown } from 'lucide-react'

const LanguageToggle: React.FC = () => {
  const { language, setLanguage, isRTL } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const languages = [
    { 
      code: 'en', 
      name: 'English', 
      shortName: 'UK',
      flag: (
        <svg className="w-5 h-4" viewBox="0 0 640 480">
          <g fillRule="evenodd">
            <path fill="#012169" d="M0 0h640v480H0z"/>
            <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l238-178L0 64V0h75z"/>
            <path fill="#C8102E" d="m424 281 216 159v40L369 281h55zm-184 20 6 35L54 480H0l246-179h-6zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/>
            <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
            <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>
          </g>
        </svg>
      )
    },
    { 
      code: 'ar', 
      name: 'العربية', 
      shortName: 'SA',
      flag: (
        <svg className="w-5 h-4" viewBox="0 0 900 600">
          <rect width="900" height="600" fill="#006C35"/>
          <g fill="white" transform="translate(450,300)">
            <g>
              <path d="M-180,-120 L-180,-60 L-120,-60 L-120,-120 Z M-160,-100 L-140,-100 M-160,-80 L-140,-80"/>
              <text x="-150" y="-70" fontSize="24" textAnchor="middle" fontFamily="serif">لا إله إلا الله محمد رسول الله</text>
            </g>
            <path d="M-60,40 L60,40 L60,80 L-60,80 Z" stroke="white" strokeWidth="2" fill="none"/>
          </g>
        </svg>
      )
    }
  ]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLanguageChange = (langCode: 'en' | 'ar') => {
    setLanguage(langCode)
    setIsOpen(false)
  }

  const currentLanguage = languages.find(lang => lang.code === language)

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center px-3 py-2 rounded-lg bg-secondary-100 dark:bg-white text-secondary-700 dark:text-black hover:bg-secondary-200 dark:hover:bg-gray-100 transition-colors duration-200 font-medium gap-3 ${
          isRTL ? 'flex-row-reverse' : ''
        }`}
        aria-label="Select language"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="flex items-center flex-shrink-0">{currentLanguage?.flag}</div>
        <span className="text-sm">{currentLanguage?.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 ${
          isRTL ? 'left-0' : 'right-0'
        }`}>
          <div className="py-1">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code as 'en' | 'ar')}
                className={`w-full flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors gap-3 ${
                  isRTL ? 'flex-row-reverse' : ''
                } ${
                  language === lang.code ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                }`}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <div className="flex items-center flex-shrink-0">{lang.flag}</div>
                <span className="font-medium">{lang.name}</span>
                {language === lang.code && (
                  <div className={`w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full ${
                    isRTL ? 'mr-auto' : 'ml-auto'
                  }`}></div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default LanguageToggle